import React from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { Wallet, Wifi, Settings2, Snowflake } from "lucide-react";

export default function VirtualCard({ wallet, onManage }) {
  const { t, lang } = useApp();
  const last4 = (wallet?.wallet_number || "0000").slice(-4);
  const frozen = !!wallet?.card_frozen;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide">{t.myCard}</h3>
        <button
          data-testid="card-manage-btn"
          onClick={onManage}
          className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          <Settings2 className="w-3.5 h-3.5" /> {t.manageCard}
        </button>
      </div>
      <motion.div
        data-testid="virtual-card-container"
        onClick={onManage}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`relative overflow-hidden rounded-3xl p-5 aspect-[1.7/1] border shadow-xl text-white cursor-pointer ${
          frozen
            ? "bg-gradient-to-br from-slate-700 via-slate-600 to-slate-500 border-white/10"
            : "bg-gradient-to-br from-slate-900 via-emerald-950 to-emerald-800 border-emerald-500/20"
        }`}
      >
        <div className="absolute inset-0 card-shine pointer-events-none opacity-70" />
        <div className="absolute top-1/2 -translate-y-1/2 end-6 w-32 h-32 rounded-full bg-emerald-400/10 blur-2xl" />

        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-300" />
            <span className="font-extrabold tracking-tight">Sb<span className="text-emerald-300">Pay</span></span>
          </div>
          {frozen ? (
            <span data-testid="card-frozen-badge" className="flex items-center gap-1 text-[11px] font-bold bg-white/15 px-2 py-0.5 rounded-full">
              <Snowflake className="w-3.5 h-3.5" /> {t.cardFrozen}
            </span>
          ) : (
            <Wifi className="w-5 h-5 rotate-90 text-white/70" />
          )}
        </div>

        <div className="absolute inset-x-5 bottom-14">
          <div className="w-11 h-8 rounded-md bg-gradient-to-br from-yellow-200 to-yellow-500 opacity-90 mb-3" />
          <p className="font-mono text-base sm:text-lg tracking-[0.2em] text-white/90">
            •••• •••• •••• {last4}
          </p>
        </div>

        <div className="absolute inset-x-5 bottom-4 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase text-white/40 tracking-wide">{wallet?.name}</p>
            <p className="text-xs text-white/60">@{wallet?.handle}</p>
          </div>
          <div className="text-end">
            <p className="text-[10px] uppercase text-white/40 tracking-wide">{t.balance}</p>
            <p className="font-bold tabular-nums text-emerald-300">
              {formatSAR(wallet?.balance, lang)} {t.currency}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
